import { Fragment, useContext, useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Button,
  SelectInput,
  TextInput,
} from "@brainfinance/icash-component-library"
import { Loader } from "../../Utilities/Loader"

import { listParser } from "../../../Helpers/Parsers/List"

import { InstantLoanContext } from "../../../Helpers/Reducers/InstantLoan"
import { employmentDetailsParser } from "../../../Helpers/Parsers/Steps/EmploymentDetails"

import { RequestsContext } from "../../../Helpers/Reducers/Requests"
import { getStepRequest, postStepRequest } from "../../../Helpers/Requests"
import { goToNext, goToPrevious } from "../../../Helpers/Routes"
import { StepsFormContainer } from "../../Utilities/StepsFormContainer"
import { toast } from "react-toastify"

import { employmentDetailsValidators } from "../../../Helpers/Validators/Steps/EmploymentDetails"
import { validateValues } from "../../../Helpers/Validators"
import { EmploymentDetailsType, InstantLoanStateType } from "../../../Helpers/Reducers/types"
import { postMessageSender } from "../../../Helpers/PostMessage"

// details cleanup is needed for submit, progress bar etc
export const employmentCleanupDetails = (details: EmploymentDetailsType) => {
  details.companyPhone = undefined
  details.employmentTitle ||= ""
  details.employerName ||= ""
  details.timeAtCurrentJob ||= ""

  // delete unused parts
  if (!hasAJob(details)) {
    let key: keyof typeof details
    for (key in details) if (key.startsWith("job")) details[key] = undefined
    details.employmentTitle = undefined
    details.timeAtCurrentJob = undefined
    if (hasSpecialEmployerName(details)) {
      if (!getTimeReceivingRevenueFieldName(details)) details.employerName = undefined
    } else details.employerName = undefined
  }
  if (hasEmploymentInsurance(details)) {
    details.employerName = undefined
  }

  return details
}

export function EmploymentDetails() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [lists, setLists] = useState({
    employmentStatus: [],
    employmentSinceWhen: [],
  })

  const navigate = useNavigate()

  const {
    state: { employmentDetails, isApp },
    dispatch,
  } = useContext(InstantLoanContext)

  const {
    state: { token },
  } = useContext(RequestsContext)

  const { pathname } = useLocation()
  const path = pathname.substring(1)

  const handleValueChange =
    <T extends string | number | Date>(key: keyof typeof employmentDetails) =>
    (value?: T) => {
      setErrors({})
      dispatch({
        step: "employmentDetails",
        payload: employmentCleanupDetails({ ...employmentDetails, [key]: value }),
      })
    }

  const handleSubmit = () => {
    const employmentDetailsValues = employmentCleanupDetails(employmentDetailsParser(employmentDetails))
    let validationErrors = validateValues(
      employmentDetailsValues,
      employmentDetailsValidators as {}
    )
    if (validationErrors) {
      // scroll to first document error
      setTimeout(
        () =>
          document
            .querySelector(".global--input-error")
            ?.scrollIntoView({ behavior: "smooth", block: "center" })
      )
      return setErrors(validationErrors)
    }
    postMessageSender({
      event: "marketing",
      payload: {
        status: "track",
        data: {
          event: "app_employment_information_submitted",
          skipCleverTap: true
        },
      },
    })

    setIsSubmitting(true)
    postStepRequest(3, employmentDetailsValues, token)
      .then(() => {
        postMessageSender({
          event: "marketing",
          payload: {
            status: "track",
            data: {
              event: "app_employment_information_completed",
            },
          },
        })
        goToNext(navigate, path)
      })
      .catch((e) => {
        toast(e.message || e, { type: "error" })
        // scroll to top of document
        document.querySelector("#scroll-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })
        setIsSubmitting(false)
      })
  }

  useEffect(() => {
    window.scrollTo(0, 0)

    getStepRequest(3, token)
      .then(({ data, list }) => {
        // initialize options for selects
        for (let name in list) list[name] = listParser(list[name])
        setLists(list)
        dispatch({
          step: "employmentDetails",
          payload: employmentCleanupDetails({
            ...data,
          }),
        })
      })
      .then(() => setIsLoading(false))
      .catch((e) => {
        toast(e.message || e, { type: "error" })
        // scroll to top of document
        document.querySelector("#scroll-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" })
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    postMessageSender({
      event: "marketing",
      payload: {
        status: "screen",
        data: {
          categoryName: "Application",
          pathName: "app_employment_information_loaded",
        },
      },
    })
  }, [])

  return (
    <Loader loading={isLoading || !token}>
      <StepsFormContainer>
        <p className="caption-medium !mb-8" id="test-caption-employer">
          Please enter your employment details. Don't worry, we won't be contacting your employer.
          Our system simply needs to perform an automated validation of the information you provide.
        </p>
        <div
          className={`grid grid-cols-2 ${
            !hasAJob(employmentDetails) ? "sm:grid-cols-1" : ""
          } gap-x-[22px] mb-[22px] items-start sm:items-start`}
        >
          <SelectInput
            native={isApp ? false : undefined}
            label="Employment status"
            value={employmentDetails.employmentStatus}
            error={errors.employmentStatus}
            onChange={handleValueChange("employmentStatus")}
            options={lists.employmentStatus}
            id="test-employment-status"
            disabled={isSubmitting}
          />
          <div className="col-span-2 sm:col-span-1 mb-4 sm:mb-0" />

          {hasEmploymentInsurance(employmentDetails) && (
            <div className="bg-[#D8F2EA] p-[16px] col-span-2 rounded-[8px]">
              <p className="caption-medium-no-colors !text-[#13141A]">
                Good news! You can still be approved for a loan while receiving employment insurance benefits.
              </p>
            </div>
          )}

          {hasAJob(employmentDetails) ? (
            <Fragment>
              <TextInput
                label="Job title"
                value={employmentDetails.employmentTitle}
                error={errors.employmentTitle}
                onChange={handleValueChange("employmentTitle")}
                id="test-job-title"
                disabled={isSubmitting}
              />
              <TextInput
                label="Company name"
                value={employmentDetails.employerName}
                error={errors.employerName}
                onChange={handleValueChange("employerName")}
                id="test-company-name"
                disabled={isSubmitting}
              />
              <SelectInput
                native={isApp ? false : undefined}
                label="Time spent at this job"
                value={employmentDetails.timeAtCurrentJob}
                error={errors.timeAtCurrentJob}
                onChange={handleValueChange("timeAtCurrentJob")}
                options={lists.employmentSinceWhen}
                id="test-job-time-spent"
                disabled={isSubmitting}
              />
            </Fragment>
          ) : null}

          {hasSpecialEmployerName(employmentDetails) && (
            <Fragment>
              {getTimeReceivingRevenueFieldName(employmentDetails) != null && (
                <TextInput
                  label={getTimeReceivingRevenueFieldName(employmentDetails) ?? ""}
                  value={employmentDetails.employerName}
                  error={errors.employerName}
                  onChange={handleValueChange("employerName")}
                  id="test-job-employer-name"
                  disabled={isSubmitting}
                />
              )}
            </Fragment>
          )}

        </div>
        <div className="flex sm:space-x-0 flex-col">
          <p className="caption !mb-[32px] sm:!mb-[24px]">
            By clicking Continue, I confirm that the information provided is current and accurate.
          </p>
          <div className="flex space-x-[22px] sm:flex-col-reverse sm:space-x-0">
            <Button
              size="large"
              className="!flex-1 sm:!mt-[22px] sm:!py-[13px] sm:!text-[16px] sm:!hidden"
              onClick={() => goToPrevious(navigate, path)}
              disabled={isSubmitting}
            >
              Previous step
            </Button>
            <Button
              size="large"
              className="!flex-1 sm:!py-[13px] sm:!text-[16px]"
              appearance="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={!!Object.values(errors).join("") || isSubmitting}
            >
              Continue
            </Button>
          </div>
        </div>
      </StepsFormContainer>
    </Loader>
  )
}

const getTimeReceivingRevenueFieldName = (
  employmentDetails: InstantLoanStateType["employmentDetails"]
) => {
  if (String(employmentDetails.employmentStatus) === "19") return "Pensioner name"
  if (String(employmentDetails.employmentStatus) === "20") return "Insurance company name"
  return null
}

// Armed forces (12) is excluded — those fields are not shown for that status
export const hasAJob = (employmentDetails: InstantLoanStateType["employmentDetails"]) => {
  if (
    String(employmentDetails.employmentStatus) === "1" ||
    String(employmentDetails.employmentStatus) === "2" ||
    String(employmentDetails.employmentStatus) === "3" ||
    String(employmentDetails.employmentStatus) === "4" ||
    String(employmentDetails.employmentStatus) === "5"
  ) {
    return true
  }
  return false
}

export const hasSpecialEmployerName = (
  employmentDetails: InstantLoanStateType["employmentDetails"]
) => {
  if (
    String(employmentDetails.employmentStatus) === "13" ||
    String(employmentDetails.employmentStatus) === "15" ||
    String(employmentDetails.employmentStatus) === "14" ||
    String(employmentDetails.employmentStatus) === "16" ||
    String(employmentDetails.employmentStatus) === "17" ||
    String(employmentDetails.employmentStatus) === "19" ||
    String(employmentDetails.employmentStatus) === "20" ||
    String(employmentDetails.employmentStatus) === "22" ||
    String(employmentDetails.employmentStatus) === "23"
  ) {
    return true
  }
  return false
}

export const hasEmploymentInsurance = (
  employmentDetails: InstantLoanStateType["employmentDetails"]
) => {
  return (
    String(employmentDetails.employmentStatus) === "9" ||
    String(employmentDetails.employmentStatus) === "18" ||
    String(employmentDetails.employmentStatus) === "21"
  )
}
